/**
 * Animated Timeline Journey Component
 * 
 * An immersive, interactive timeline showing project development stages:
 * - Scroll-triggered animations
 * - Interactive milestone cards
 * - Progress visualization
 * - Animated transitions between stages
 * - Visual storytelling of project journey
 */

'use client';

import React from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import type { PropertyDevelopmentProject } from '@/lib/api/clients/projects';

export interface TimelineStage {
  stage: string;
  date: string | null;
  title: string;
  description: string;
  status: 'completed' | 'in_progress' | 'upcoming';
  icon: React.ReactNode;
}

export interface AnimatedTimelineProps {
  project: PropertyDevelopmentProject;
  messages: {
    timeline: string;
    projectJourney: string;
    planning: string;
    planningDesc: string;
    approvals: string;
    approvalsDesc: string;
    preLaunch: string;
    preLaunchDesc: string;
    construction: string;
    constructionDesc: string;
    completion: string;
    completionDesc: string;
    completed: string;
    inProgress: string;
    upcoming: string;
  };
  accentColor?: string;
  className?: string;
}

/**
 * Animated Timeline showing project development stages
 */
export function AnimatedTimeline({
  project,
  messages,
  accentColor = '#BBA339',
  className = '',
}: AnimatedTimelineProps) {
  const timelineRef = useRef(null);
  const isInView = useInView(timelineRef, { once: true, amount: 0.2 });

  // Map project stage to timeline stages
  const stages: TimelineStage[] = [
    {
      stage: 'PLANNING',
      date: project.developmentApprovalDate,
      title: messages.planning,
      description: messages.planningDesc,
      status: ['PLANNING', 'CONCEPT', 'FEASIBILITY_STUDY'].includes(project.stage || '') 
        ? 'in_progress' 
        : project.stage ? 'completed' : 'upcoming',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
    },
    {
      stage: 'APPROVALS',
      date: project.developmentApprovalDate,
      title: messages.approvals,
      description: messages.approvalsDesc,
      status: project.developmentApprovalStatus === 'APPROVED' ? 'completed' : 'in_progress',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      stage: 'PRE_LAUNCH',
      date: project.estimatedStartDate,
      title: messages.preLaunch,
      description: messages.preLaunchDesc,
      status: project.stage === 'PRE_LAUNCH_MARKETING' ? 'in_progress' : 
              ['LAUNCH_SALES', 'UNDER_CONSTRUCTION', 'CONSTRUCTION_COMPLETED'].includes(project.stage || '') ? 'completed' : 'upcoming',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        </svg>
      ),
    },
    {
      stage: 'CONSTRUCTION',
      date: project.estimatedStartDate,
      title: messages.construction,
      description: messages.constructionDesc,
      status: project.stage === 'UNDER_CONSTRUCTION' ? 'in_progress' :
              project.stage === 'CONSTRUCTION_COMPLETED' ? 'completed' : 'upcoming',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
    },
    {
      stage: 'COMPLETION',
      date: project.actualCompletionDate || project.estimatedCompletionDate,
      title: messages.completion,
      description: messages.completionDesc,
      status: project.actualCompletionDate ? 'completed' : 'upcoming',
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
    },
  ];

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  return (
    <div ref={timelineRef} className={`py-16 ${className}`}>
      {/* Header */}
      <motion.div
        className="mb-12 text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <h2 className="mb-3 text-4xl font-bold text-gray-900">
          {messages.timeline}
        </h2>
        <p className="text-lg text-gray-600">{messages.projectJourney}</p>
      </motion.div>

      {/* Timeline */}
      <div className="relative mx-auto max-w-6xl">
        {/* Animated Progress Line */}
        <div className="absolute left-1/2 top-0 h-full w-1 -translate-x-1/2 bg-gray-200">
          <motion.div
            className="h-full w-full origin-top"
            style={{ backgroundColor: accentColor }}
            initial={{ scaleY: 0 }}
            animate={isInView ? { scaleY: 1 } : {}}
            transition={{ duration: 2, ease: 'easeInOut' }}
          />
        </div>

        {/* Timeline Items */}
        <div className="space-y-12">
          {stages.map((stage, index) => {
            const isLeft = index % 2 === 0;
            
            return (
              <motion.div
                key={stage.stage}
                className={`relative flex items-center ${
                  isLeft ? 'flex-row' : 'flex-row-reverse'
                }`}
                initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                {/* Content Card */}
                <motion.div
                  className={`w-5/12 ${isLeft ? 'pr-8 text-right' : 'pl-8 text-left'}`}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-lg transition-shadow duration-300 hover:shadow-2xl">
                    {/* Status Indicator */}
                    <div className={`mb-4 flex items-center gap-2 ${isLeft ? 'justify-end' : 'justify-start'}`}>
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                          stage.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : stage.status === 'in_progress'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {stage.status === 'completed' && messages.completed}
                        {stage.status === 'in_progress' && messages.inProgress}
                        {stage.status === 'upcoming' && messages.upcoming}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="mb-2 text-xl font-bold text-gray-900">
                      {stage.title}
                    </h3>

                    {/* Date */}
                    {stage.date && (
                      <p className="mb-3 text-sm font-medium" style={{ color: accentColor }}>
                        {formatDate(stage.date)}
                      </p>
                    )}

                    {/* Description */}
                    <p className="text-sm text-gray-600">{stage.description}</p>

                    {/* Hover Effect */}
                    <motion.div
                      className="absolute inset-x-0 bottom-0 h-1"
                      style={{ backgroundColor: accentColor }}
                      initial={{ scaleX: 0 }}
                      whileHover={{ scaleX: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                </motion.div>

                {/* Center Icon */}
                <motion.div
                  className="absolute left-1/2 z-10 -translate-x-1/2"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={isInView ? { scale: 1, rotate: 0 } : {}}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.2 + 0.3,
                    type: 'spring',
                    stiffness: 200,
                  }}
                  whileHover={{ scale: 1.2, rotate: 5 }}
                >
                  <div
                    className={`flex h-16 w-16 items-center justify-center rounded-full text-white shadow-xl ${
                      stage.status === 'completed'
                        ? 'bg-green-600'
                        : stage.status === 'in_progress'
                        ? 'bg-blue-600'
                        : 'bg-gray-400'
                    }`}
                    style={
                      stage.status === 'in_progress'
                        ? { backgroundColor: accentColor }
                        : {}
                    }
                  >
                    {stage.icon}
                  </div>

                  {/* Pulsing Ring for In Progress */}
                  {stage.status === 'in_progress' && (
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{ border: `3px solid ${accentColor}` }}
                      animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.8, 0, 0.8],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                  )}
                </motion.div>

                {/* Empty Space on Other Side */}
                <div className="w-5/12" />
              </motion.div>
            );
          })}
        </div>

        {/* Completion Badge */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: stages.length * 0.2 }}
        >
          {project.actualCompletionDate && (
            <div className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-green-500 to-emerald-600 px-8 py-4 text-white shadow-2xl">
              <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-lg font-bold">Project Completed</span>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

